import { getMeta, setMeta, META_KEYS } from "../local/db";
import type { IsoTimestamp } from "../../domain/time";

/**
 * Clock-skew detection (T098, FR-050).
 *
 * A terminal timestamps every sale from its own clock, offline, hours before a
 * server ever sees it. If that clock is wrong, the sales are not wrong — they
 * happened, the money is real, the receipts are printed — but their PLACE IN
 * TIME is wrong, and that quietly corrupts:
 *
 *   * which business date a sale lands on, hence which Z reading it appears in
 *   * the order two terminals' sales appear to have occurred in
 *   * ticket ageing on the kitchen display
 *
 * So the terminal clock is never corrected and never used to correct data.
 * Skew is MEASURED against `server_time` and recorded, and affected records are
 * flagged for a human. Silently rewriting timestamps would destroy the evidence
 * that anything was ever wrong.
 */

/**
 * Beyond this, a sale can land on the wrong business date or in the wrong
 * shift, which is the point at which the figures a manager reconciles stop
 * matching the day they actually happened.
 */
export const SKEW_WARNING_MS = 120_000;

/**
 * Beyond this, the clock is not drifting — it is wrong. Typically a device that
 * lost power and came back at its epoch default.
 */
export const SKEW_CRITICAL_MS = 900_000;

export type SkewSeverity = "ok" | "warning" | "critical";

export interface ClockReading {
  /** Positive means the TERMINAL is ahead of the server. */
  offsetMs: number;
  severity: SkewSeverity;
  measuredAt: IsoTimestamp;
}

export function severityFor(offsetMs: number): SkewSeverity {
  const magnitude = Math.abs(offsetMs);
  if (magnitude >= SKEW_CRITICAL_MS) return "critical";
  if (magnitude >= SKEW_WARNING_MS) return "warning";
  return "ok";
}

/**
 * Measure skew from a sync response.
 *
 * `serverTime` is read at the START of the round trip's response handling, so
 * the measurement carries network latency — typically tens of milliseconds,
 * three orders of magnitude below the warning threshold. Correcting for it
 * would add precision the thresholds do not need.
 */
export function measureSkew(serverTime: string, now = Date.now()): ClockReading {
  const offsetMs = now - new Date(serverTime).getTime();

  return {
    offsetMs,
    severity: severityFor(offsetMs),
    measuredAt: new Date(now).toISOString(),
  };
}

export async function recordSkew(reading: ClockReading): Promise<void> {
  await setMeta(META_KEYS.serverTimeOffsetMs, reading.offsetMs);
  await setMeta(META_KEYS.clockSkew, reading);
}

export async function getLastSkew(): Promise<ClockReading | undefined> {
  return getMeta<ClockReading>(META_KEYS.clockSkew);
}

/**
 * The server's idea of now, from the terminal's clock plus the last measured
 * offset.
 *
 * Used for ticket ageing and anything else that must be consistent ACROSS
 * devices. Never used to write a sale timestamp: a sale records when the
 * terminal believed it happened, and the skew flag is what tells a human that
 * belief was off.
 */
export async function serverNow(): Promise<IsoTimestamp> {
  const offset = (await getMeta<number>(META_KEYS.serverTimeOffsetMs)) ?? 0;
  return new Date(Date.now() - offset).toISOString();
}

/**
 * Whether records created during an outage should be flagged.
 *
 * Called with the skew measured on the FIRST sync after reconnecting, which is
 * the first moment the terminal can know its clock was wrong. Everything
 * written while offline is suspect, because the skew was present the whole
 * time and nothing could detect it.
 */
export function shouldFlagOfflineRecords(reading: ClockReading): boolean {
  return reading.severity !== "ok";
}
